import { useCallback } from 'react';

import { useAppContext } from '../../../store/App/utils/useAppContext';
import { messageReactionFail, messageReactionLocal, messageReactionSuccess } from '../../../store/transcript/actions';
import { useTranscriptContext } from '../../../store/transcript/utils/useTranscriptContext';
import { MessageStatus } from '../types/messages';

export const useMessageActions = () => {
  const { state, dispatch } = useTranscriptContext();
  const { endpoints } = useAppContext();

  /**
   * Handle message liked
   * @param messageId {string} - Message id
   * @param liked {boolean} - Message liked or disliked
   * @returns {Promise<boolean>} - true if success
   */
  const handleMessageReaction = async (
    messageId: string,
    status: MessageStatus,
  ): Promise<boolean> => {
    return endpoints.handleMessageReaction(messageId, status, state.sessionId);
  };

  const handleMessageStatusChange = async (
    messageId: string,
    status: MessageStatus,
  ) => {
    dispatch(messageReactionLocal({ messageId, status }));
    handleMessageReaction(messageId, status).then(() => {
      dispatch(messageReactionSuccess({ messageId, status }));
    }).catch(() => {
      dispatch(messageReactionFail(messageId));
    });
  };

  /**
   * Send server message that message was copied
   * @param messageId {string} - Message id
   * @returns {Promise<boolean>} - true if success
   */
  const handleMessageCopied = useCallback(
    async (messageId: string) => {
      return endpoints.handleMessageCopied(messageId, state.sessionId).catch(() => {
        // TODO Handle copy action error
      });
    },
    [endpoints, state.sessionId],
  );

  /**
   * Copy text to clipboard
   * @param text {string} - Text to copy to clipboard
   * @param messageId {string} - Message id
   * @returns {Promise<boolean>} - true if success
   */
  const copyToClipboard = useCallback(
    async (text: string, messageId: string) => {
      try {
        await navigator.clipboard.writeText(text);
        return handleMessageCopied(messageId);
      } catch {
        // TODO Handle copy action error
      }
    },
    [handleMessageCopied],
  );

  return {
    handleMessageStatusChange,
    copyToClipboard,
  };
};
